import { Button, styled, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { Multiselect } from '../../components/Multiselect/Multiselect';
import { SearchInput } from '../../components/SearchInput/SearchInput';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import { Typography } from '../../components/Typography/Typography';
import { TableSkeletonLoader } from '../../components/SkeletonLoader/Table.SkeletonLoader';
import { useIcons } from '../../hooks/useIcons';
import { useToastMessage } from '../../hooks/useToastMessage';
import { ApprovalDelegate, useApprovalDelegates } from '../../queries/hooks/useApprovalDelegates';
import { useRemoveApprovalDelegates } from '../../queries/hooks/useRemoveApprovalDelegates';
import { useDeals } from '../../queries/hooks/useDeals';
import { useUsers } from '../../queries/hooks/useUsers';
import { isSmallScreenState } from '../../state/UIState';
import { Deal, SelectItem, UserRole } from '../../types';
import { hasRole } from '../../utils/roles';
import { extractErrorMessage } from '../../utils/errorNotifier';
import { PageHeader } from '../layout/PageHeader';
import { ContentWrapper } from '../layout/styled';
import { DelegationsTable, OwnerGroup } from './components/DelegationsTable';
import { DelegatesBulkActions } from './components/DelegatesBulkActions';
import { DelegateModal } from './components/DelegateModal';
import { ReassignDelegatesModal } from './components/ReassignDelegatesModal';

const pairKey = (dealId: number, opId: number) => `${dealId}:${opId}`;

export const ApprovalDelegates = () => {
  const { colors } = useTheme();
  const { DownLoadIcon } = useIcons();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const { pushSuccessToast, pushErrorToast } = useToastMessage();

  const { data: delegates = [], isLoading: delegatesLoading } = useApprovalDelegates();
  const { data: deals = [], isLoading: dealsLoading } = useDeals(false, UserRole.ADMIN);
  const { data: users = [], isLoading: usersLoading } = useUsers('active');
  const removeDelegates = useRemoveApprovalDelegates();

  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<SelectItem[]>([]);
  const [opFilter, setOpFilter] = useState<SelectItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [removeTarget, setRemoveTarget] = useState<ApprovalDelegate[] | null>(null);
  const [delegateOwnerId, setDelegateOwnerId] = useState<number | null>(null);
  const [reassignTargets, setReassignTargets] = useState<ApprovalDelegate[] | null>(null);

  const isLoading = delegatesLoading || dealsLoading || usersLoading;

  // All active deal owners (the table lists one group per owner, even with zero delegations).
  const dealOwners = useMemo(
    () =>
      users
        .filter((u) => u.isActive && hasRole(u, UserRole.DEAL_OWNER))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  // Existing delegations grouped by the source (from) deal owner.
  const delegationsByOwner = useMemo(() => {
    const map = new Map<number, ApprovalDelegate[]>();
    for (const d of delegates) {
      const list = map.get(d.fromDealOwner.id) ?? [];
      list.push(d);
      map.set(d.fromDealOwner.id, list);
    }
    return map;
  }, [delegates]);

  // Deals owned by each owner (owner.id -> deals), from the deal list.
  const dealsByOwner = useMemo(() => {
    const map = new Map<number, Deal[]>();
    for (const deal of deals) {
      for (const owner of deal.dealOwners ?? []) {
        const list = map.get(owner.id) ?? [];
        list.push(deal);
        map.set(owner.id, list);
      }
    }
    return map;
  }, [deals]);

  // Filter option lists.
  const ownerOptions = useMemo<SelectItem[]>(
    () => dealOwners.map((o) => ({ id: o.id, value: o.name })),
    [dealOwners]
  );
  const opOptions = useMemo<SelectItem[]>(() => {
    const seen = new Map<number, SelectItem>();
    for (const deal of deals) {
      for (const op of deal.operatingPartners ?? []) {
        if (!seen.has(op.id)) seen.set(op.id, { id: op.id, value: op.name });
      }
    }
    return [...seen.values()].sort((a, b) => a.value.localeCompare(b.value));
  }, [deals]);

  // Build one group per deal owner: unfiltered counts + filtered nested rows + delegate availability.
  const groups = useMemo<OwnerGroup[]>(() => {
    const term = search.trim().toLowerCase();
    const ownerIds = new Set(ownerFilter.map((o) => Number(o.id)));
    const opIds = new Set(opFilter.map((o) => Number(o.id)));

    return dealOwners
      .filter((owner) => ownerIds.size === 0 || ownerIds.has(owner.id))
      .map((owner) => {
        const delegs = delegationsByOwner.get(owner.id) ?? [];
        const ownerDeals = dealsByOwner.get(owner.id) ?? [];
        const totalCount = ownerDeals.reduce((sum, d) => sum + (d.operatingPartners?.length ?? 0), 0);
        const delegatedPairs = new Set(delegs.map((d) => pairKey(d.deal.id, d.operatingPartner.id)));
        const canDelegate = ownerDeals.some((d) =>
          (d.operatingPartners ?? []).some((op) => !delegatedPairs.has(pairKey(d.id, op.id)))
        );

        const rows = delegs.filter((r) => {
          if (opIds.size && !opIds.has(r.operatingPartner.id)) return false;
          if (!term) return true;
          return [r.deal.name, r.operatingPartner.name, r.toDealOwner.name]
            .filter(Boolean)
            .some((field) => field.toLowerCase().includes(term));
        });

        return {
          owner: { id: owner.id, name: owner.name },
          rows,
          delegatedCount: delegs.length,
          totalCount,
          canDelegate,
        };
      });
  }, [dealOwners, delegationsByOwner, dealsByOwner, search, ownerFilter, opFilter]);

  // Delegations visible after filters (for bulk actions + export).
  const filteredRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  // For the open Delegate modal: the owner + their still-delegatable deals and per-deal OPs.
  const delegateOwner = useMemo(
    () => dealOwners.find((o) => o.id === delegateOwnerId) ?? null,
    [dealOwners, delegateOwnerId]
  );
  const { availableDeals, availableOpsByDeal } = useMemo(() => {
    if (delegateOwnerId == null) return { availableDeals: [] as SelectItem[], availableOpsByDeal: {} as Record<number, SelectItem[]> };
    const delegatedPairs = new Set(
      (delegationsByOwner.get(delegateOwnerId) ?? []).map((d) => pairKey(d.deal.id, d.operatingPartner.id))
    );
    const ownerDeals = dealsByOwner.get(delegateOwnerId) ?? [];
    const byDeal: Record<number, SelectItem[]> = {};
    const dealsList: SelectItem[] = [];
    for (const deal of ownerDeals) {
      const ops = (deal.operatingPartners ?? []).filter((op) => !delegatedPairs.has(pairKey(deal.id, op.id)));
      if (ops.length > 0) {
        dealsList.push({ id: deal.id, value: deal.name });
        byDeal[deal.id] = ops.map((op) => ({ id: op.id, value: op.name }));
      }
    }
    dealsList.sort((a, b) => a.value.localeCompare(b.value));
    return { availableDeals: dealsList, availableOpsByDeal: byDeal };
  }, [delegateOwnerId, delegationsByOwner, dealsByOwner]);

  const selectedVisibleCount = filteredRows.filter((r) => selectedIds.has(r.id)).length;

  const toggleRow = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleManyRows = (ids: number[], select: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const resetFilters = () => {
    setSearch('');
    setOwnerFilter([]);
    setOpFilter([]);
  };

  const exportCsv = () => {
    const header = ['Deal Owner', 'Deal', 'Operating Partner', 'Delegated To'];
    const escape = (value: string) => `"${(value ?? '').replace(/"/g, '""')}"`;
    const lines = filteredRows.map((d) =>
      [d.fromDealOwner.name, d.deal.name, d.operatingPartner.name, d.toDealOwner.name].map(escape).join(',')
    );
    const csv = [header.map(escape).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'approval-delegates.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const confirmRemove = () => {
    if (!removeTarget) return;
    const ids = removeTarget.map((d) => d.id);
    removeDelegates.mutate(ids, {
      onSuccess: () => {
        pushSuccessToast({
          message: `Removed ${ids.length} delegation${ids.length === 1 ? '' : 's'}.`,
        });
        toggleManyRows(ids, false);
        setRemoveTarget(null);
      },
      onError: (error) => {
        pushErrorToast({ message: extractErrorMessage(error) });
        setRemoveTarget(null);
      },
    });
  };

  const hasFiltersApplied = !!search || ownerFilter.length > 0 || opFilter.length > 0;

  return (
    <>
      <PageHeader
        title='Approval Delegates'
        description='Delegate a deal owner’s approvals to someone else'
        breadcrumbs={[{ title: 'Administration' }, { title: 'Approval Delegates' }]}
        actionComponent={
          <Button
            variant='outlined'
            onClick={exportCsv}
            disabled={filteredRows.length === 0}
            startIcon={<DownLoadIcon />}
            style={{
              backgroundColor: colors.surfaceBackground.bg1,
              marginBottom: isSmallScreen ? '10px' : '0px',
            }}
          >
            Export
          </Button>
        }
      />
      <ContentWrapper isSmallScreen={isSmallScreen}>
        {isLoading ? (
          <TableSkeletonLoader />
        ) : (
          <>
            <FiltersBar>
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => setSearch('')}
                placeholder='Search deal, OP or delegate'
                style={{ width: isSmallScreen ? '100%' : '280px', height: '36px' }}
              />
              <FilterField>
                <Multiselect
                  options={ownerOptions}
                  value={ownerFilter}
                  onChange={(_, value) => setOwnerFilter(value ?? [])}
                  fieldPlaceholder='Deal Owner'
                  optionName='Deal Owner'
                  width='220px'
                  sx={{ width: isSmallScreen ? '100%' : '220px' }}
                />
              </FilterField>
              <FilterField>
                <Multiselect
                  options={opOptions}
                  value={opFilter}
                  onChange={(_, value) => setOpFilter(value ?? [])}
                  fieldPlaceholder='Operating Partner'
                  optionName='Operating Partner'
                  width='220px'
                  sx={{ width: isSmallScreen ? '100%' : '220px' }}
                />
              </FilterField>
              <Button
                onClick={resetFilters}
                variant='text'
                style={{ height: '28px', marginTop: '2px' }}
                disabled={!hasFiltersApplied}
                sx={{
                  '.MuiButton-startIcon': { marginRight: '2px' },
                  '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
                }}
              >
                <Typography variant='subtitle1' color={colors.textAccent.default}>
                  Reset Filters
                </Typography>
              </Button>
            </FiltersBar>

            <TableArea>
              {selectedVisibleCount > 0 && (
                <FloatingBar>
                  <DelegatesBulkActions
                    selectedCount={selectedVisibleCount}
                    onReassign={() => setReassignTargets(filteredRows.filter((d) => selectedIds.has(d.id)))}
                    onRemove={() => setRemoveTarget(filteredRows.filter((d) => selectedIds.has(d.id)))}
                    onClear={clearSelection}
                  />
                </FloatingBar>
              )}
              <DelegationsTable
                groups={groups}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleManyRows={toggleManyRows}
                onRemoveRow={(row) => setRemoveTarget([row])}
                onDelegateOwner={(ownerId) => setDelegateOwnerId(ownerId)}
              />
            </TableArea>
          </>
        )}
      </ContentWrapper>

      <ConfirmModal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title='Remove Delegation'
        confirmLabel='Remove'
        note={
          removeTarget && removeTarget.length > 1
            ? `This removes ${removeTarget.length} delegations. Approval of these deals returns to their original deal owners on all open and future timesheets.`
            : 'This removes the delegation. Approval of this deal returns to its original deal owner on all open and future timesheets.'
        }
      />

      <DelegateModal
        isOpen={delegateOwnerId != null}
        onClose={() => setDelegateOwnerId(null)}
        owner={delegateOwner ? { id: delegateOwner.id, name: delegateOwner.name } : null}
        availableDeals={availableDeals}
        availableOpsByDeal={availableOpsByDeal}
      />

      <ReassignDelegatesModal
        isOpen={!!reassignTargets}
        onClose={() => setReassignTargets(null)}
        targets={reassignTargets ?? []}
        onDone={() => {
          clearSelection();
          setReassignTargets(null);
        }}
      />
    </>
  );
};

const FiltersBar = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const FilterField = styled('div')`
  min-width: 180px;
`;

const TableArea = styled('div')`
  position: relative;
`;

// Overlays the filters row while rows are selected, so the table never moves and no space is reserved.
const FloatingBar = styled('div')`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 11;
`;
